import React from "react"
import { Col, OverlayTrigger, Row, Tooltip } from "react-bootstrap"
import BootstrapTable, { ColumnDescription } from "react-bootstrap-table-next"
import filterFactory from "react-bootstrap-table2-filter"
import ToolkitProvider, { CSVExport } from "react-bootstrap-table2-toolkit"
import { useTranslation } from "react-i18next"
import Flag from "react-world-flags"
import { useAppDispatch } from "../../app/hooks"
import { SearchBar } from "../../components/Tables/Columns"
import { countryNamesInEnglish, dateFormatter } from "../../utils/commonFunctions"

function ReadyParcelsTable({
    data,
    setSelectedRows,
    selectedRows,
    options,
    setOptions
}: {
    data: Record<string, any>[]
    setSelectedRows: Function
    selectedRows: string[]
    options: Record<string, any>
    setOptions: Function
}) {
    const { t } = useTranslation()
    const dispatch = useAppDispatch()
    const printMenifest = () => {
        // handleBtnClick()
    }
    const handleTableChange = (type: any, { page, sizePerPage }: { page: number; sizePerPage: number }) => {
        setOptions({
            ...options,
            page: {
                ...options.page,
                parcels: page
            },
            sizePerPage
        })
    }

    const onSelect = (row: Record<string, any>, isSelect: any) => {
        if (isSelect) {
            if (selectedRows.some((id) => `${id}` === `${row.id}`)) return
            setSelectedRows([...selectedRows, row.id])
        } else {
            setSelectedRows(selectedRows?.filter((sp) => `${sp}` !== `${row?.id}`))
        }
    }

    const handleSelectAll = (isSelect: any, rows: Record<string, any>[]) => {
        if (isSelect) {
            setSelectedRows(rows?.map((row) => row?.id))
        } else {
            setSelectedRows([])
        }
    }

    const selectRow: any = {
        mode: "checkbox",
        clickToSelect: false,
        onSelect: onSelect,
        onSelectAll: handleSelectAll,
        selectColumnPosition: "left",
        hideSelectAll: true,
        selected: selectedRows
    }

    const columns = [
        // {
        // 	dataField: "id",
        // 	text: t("#"),
        // },
        {
            text: t("Shipment ID"),
            dataField: "uuid",
            classes: "highlighted-text"
        },
        {
            text: t("Account ID"),
            dataField: "user",
            classes: "highlighted-text",
            formatter: (cell: Record<string, any>, row: Record<string, any>) => {
                return cell?.user_pin ? cell?.user_pin : "N/A"
            }
        },
        {
            text: t("Date"),
            dataField: "created_at",
            formatter: (cell: string, row: Record<string, any>) => {
                let date
                if (cell && cell?.length > 0) {
                    date = dateFormatter(cell, false, "")
                }
                return cell && cell?.length > 0 ? (
                    <>
                        <span className="d-block">{date}</span>
                    </>
                ) : (
                    "-"
                )
            }
        },
        {
            text: t("A. Weight & Dimensions"),
            dataField: "a-weight",
            formatter: (cell: any, row: Record<string, any>) => (
                <>
                    {row.weight_unit && typeof row.weight_value === "number" ? (
                        <>
                            <span className="fw-bold">{row.weight_value}</span>
                            &nbsp;
                            <span>{row.weight_unit}</span>
                        </>
                    ) : (
                        "-"
                    )}

                    <br />
                    {typeof row.dimension_length === "number" &&
                    typeof row.dimension_width === "number" &&
                    typeof row.dimension_height === "number" &&
                    row.dimensions_unit ? (
                        <>
                            {`${row.dimension_length}`}&#x2715;
                            {`${row.dimension_width}`}&#x2715;
                            {`${row.dimension_height}`}&nbsp;
                            {row.dimensions_unit}
                        </>
                    ) : (
                        "-"
                    )}
                </>
            )
        },
        // {
        // 	text: t("V. Weight"),
        // 	dataField: "v-weight",
        // 	formatter: (cell: any, row: Record<string, any>) => {
        //         return `${row?.vol_weight_value || "-"} ${row?.weight_unit || "lb|kg"}`
        //     },
        // },
        {
            text: t("Destination"),
            dataField: "destination",
            formatter: (_cell: string, row: Record<string, any>) => {
                const cell = row["ParcelAddress"]?.country ?? ""
                return cell && cell !== "" && countryNamesInEnglish.of(cell) ? (
                    <OverlayTrigger
                        placement="bottom"
                        delay={{ show: 250, hide: 400 }}
                        overlay={<Tooltip id={`${cell}-tooltip`}>{countryNamesInEnglish.of(cell)}</Tooltip>}>
                        <>
                            <Flag
                                className="d-inline-block mx-1"
                                style={{ width: 28, height: 18 }}
                                code={(cell ?? "").toLowerCase()}
                            />
                            &nbsp;{cell}
                        </>
                    </OverlayTrigger>
                ) : (
                    "-"
                )
            }
        },
        {
            text: t("Value"),
            dataField: "amount",
            formatter: (cell: number, row: Record<string, any>) => {
                return cell && cell > 0 ? `$${cell}` : "-"
            }
        },
        {
            text: t("Type"),
            dataField: "cod",
            formatter: (cell: number, row: Record<string, any>) => {
                return cell === 1 ? "COD" : "Prepaid"
            }
        },
        {
            text: t("Terms"),
            dataField: "terms",
            formatter: (cell: number, row: Record<string, any>) => {
                return "DDU"
            }
        },
        {
            text: t("LM AWB"),
            dataField: "new_awb_number",
            formatter: (cell: string, row: Record<string, any>) => {
                return cell?.length > 0 ? cell : "-"
            }
        },
        {
            text: t("TBA"),
            dataField: "purchase_tracking_number",
            formatter: (cell: string, row: Record<string, any>) => {
                return cell?.length > 0 ? cell : "-"
            }
        }
    ]

    const { ExportCSVButton } = CSVExport

    return (
        <div className="table-responsive">
            {columns && (
                <ToolkitProvider
                    keyField="id"
                    data={data}
                    columns={columns as ColumnDescription<any, any>[]}
                    search={{ searchFormatted: true }}
                    exportCSV={{
                        fileName: "dataTable.csv",
                        noAutoBOM: false,
                        exportAll: false,
                        onlyExportFiltered: true
                    }}>
                    {(props) => (
                        <div className="table-container my-4 container-fluid">
                            <Row className="search suites-search">
                                <Col
                                    xs={12}
                                    sm={12}
                                    md={12}
                                    lg={12}
                                    className="d-flex justify-content-end px-0">
                                    <SearchBar {...props.searchProps} />
                                </Col>
                            </Row>
                            <Row className="table-data my-3 modal-gray-bg">
                                <Col>
                                    <div
                                        style={{
                                            maxHeight: "200px",
                                            overflow: "auto"
                                        }}>
                                        <BootstrapTable
                                            {...props.baseProps}
                                            selectRow={selectRow}
                                            wrapperClasses="table-wrapper my-3 "
                                            headerClasses="header-class font-13"
                                            bordered={false}
                                            noDataIndication={t("No_Record")}
                                            filter={filterFactory()}
                                            rowClasses="custom-table-row"
                                        />
                                    </div>
                                </Col>
                            </Row>
                            {/* <Row className="mt-4 mx-1">
									<Col xs={12} sm={12} md={12} lg={12} className="text-align-end ">
										<Button variant="primary" type="submit" className="w-15" onClick={() => printMenifest()}>
											{t("Print_Manifest")}
										</Button>
									</Col>
								</Row> */}
                        </div>
                    )}
                </ToolkitProvider>
            )}
        </div>
    )
}

export default React.memo(ReadyParcelsTable)
